import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import {
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  FullscreenExit,
} from "@material-ui/icons";

import alignmentSessionStyles from "./styles";
import { getMonthAbbr } from "./utils";
import useThemeColors from "hooks/useThemeColors";

const useStyles = makeStyles(alignmentSessionStyles);

/**
 * Navigation component
 * Displays pills for page navigation with sliding background effect
 * Includes period reference selector and prev/next buttons
 */
function Navigation({
  sortedPages,
  activePage,
  onPageClick,
  onPrevious,
  onNext,
  clientColor,
  periodOptions,
  periodReference,
  onPeriodChange,
  highlightEnabled,
  onHighlightChange,
  showHighlightToggle,
  focusMode,
  onFocusModeToggle,
  dataOrchestrator,
}) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { clientTextColor } = useThemeColors();

  // Refs for pills sliding background effect
  const pillsRef = useRef({});
  const [bgStyle, setBgStyle] = useState({ left: 0, width: 0 });

  // Period menu state
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const periodMenuRef = useRef(null);

  const currentIndex = sortedPages.findIndex((p) => p.id === activePage);
  const isFirstPage = currentIndex === 0;
  const isLastPage = currentIndex === sortedPages.length - 1;

  // Current period label
  const currentPeriodLabel =
    periodOptions.find((opt) => opt.key === periodReference)?.label || "";

  // Build comparison period description (e.g. "jan/24 a nov/24 vs jan/25 a nov/25")
  // Uses the orchestrator's getComparisonPeriods() which supports all 8 period types.
  const comparisonLabel = useMemo(() => {
    const periods = dataOrchestrator?.getComparisonPeriods?.();
    if (!periods || !periods.previous?.length || !periods.current?.length)
      return "";

    const fmtRange = (arr) => {
      if (!arr || arr.length === 0) return "";
      // Sort to get oldest and newest
      const sorted = [...arr].sort(
        (a, b) => a[0] * 100 + a[1] - (b[0] * 100 + b[1]),
      );
      if (sorted.length === 1) {
        return `${getMonthAbbr(sorted[0][1] - 1, t)}/${String(sorted[0][0]).slice(-2)}`;
      }
      const f = sorted[0];
      const l = sorted[sorted.length - 1];
      return `${getMonthAbbr(f[1] - 1, t)}/${String(f[0]).slice(-2)} a ${getMonthAbbr(l[1] - 1, t)}/${String(l[0]).slice(-2)}`;
    };

    return `${fmtRange(periods.previous)} vs ${fmtRange(periods.current)}`;
  }, [dataOrchestrator, t]);

  // Function to update background position
  const updateBgPosition = useCallback(() => {
    const activeEl = pillsRef.current[activePage];
    if (activeEl) {
      const parent = activeEl.parentElement;
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        const activeRect = activeEl.getBoundingClientRect();
        setBgStyle({
          left: activeRect.left - parentRect.left,
          width: activeRect.width,
        });
      }
    }
  }, [activePage]);

  // Update background position when active page or language changes
  useEffect(() => {
    const timer = setTimeout(updateBgPosition, 10);
    window.addEventListener("resize", updateBgPosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateBgPosition);
    };
  }, [activePage, sortedPages, i18n.language, updateBgPosition]);

  // Close period menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        periodMenuRef.current &&
        !periodMenuRef.current.contains(event.target)
      ) {
        setPeriodMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={classes.navigationWrapper}>
      <div className={classes.pillsContainer}>
        {/* Left toggle container (highlight switch + comparison label) */}
        {showHighlightToggle && (
          <div className={classes.navLeftContainer}>
            <div className={classes.toggleSwitchWrapper}>
              <span className={classes.toggleSwitchLabel}>
                {t("alignment_session.by_dimension.highlight")}
              </span>
              <label className={classes.toggleSwitch}>
                <input
                  type="checkbox"
                  className={classes.toggleSwitchInput}
                  checked={highlightEnabled}
                  onChange={(e) => onHighlightChange(e.target.checked)}
                />
                <span
                  className={`${classes.toggleSwitchSlider} ${
                    highlightEnabled ? classes.toggleSwitchSliderChecked : ""
                  }`}
                  style={
                    highlightEnabled
                      ? { backgroundColor: clientColor }
                      : undefined
                  }
                />
              </label>
            </div>
            {comparisonLabel && (
              <span className={classes.comparisonLabel}>{comparisonLabel}</span>
            )}
          </div>
        )}
        {/* Comparison label only shown on pages with highlight toggle */}

        <div className={classes.pillsWrapper}>
          {/* Background slider */}
          <div
            className={classes.pillsBackground}
            style={{
              left: bgStyle.left,
              width: bgStyle.width,
              backgroundColor: clientColor,
            }}
          />

          {/* Pills */}
          {sortedPages.map((page) => {
            const isActive = activePage === page.id;
            return (
              <div
                key={page.id}
                ref={(el) => (pillsRef.current[page.id] = el)}
                className={`${classes.pillItem} ${isActive ? classes.pillItemActive : classes.pillItemInactive}`}
                onClick={() => onPageClick(page.id)}
              >
                <span
                  className={classes.pillText}
                  style={isActive ? { color: clientTextColor } : undefined}
                >
                  {t(`alignment_session.topics.${page.key}`)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Navigation buttons */}
        <div className={classes.navButtonsContainer}>
          {/* Period reference button */}
          <div className={classes.periodReferenceContainer} ref={periodMenuRef}>
            <div
              className={classes.periodReferenceButton}
              onClick={() => setPeriodMenuOpen(!periodMenuOpen)}
            >
              <span>período: {currentPeriodLabel}</span>
              <ExpandMore style={{ fontSize: "16px" }} />
            </div>
            {periodMenuOpen && (
              <div className={classes.periodReferenceMenu}>
                {periodOptions.map((option) => (
                  <div
                    key={option.key}
                    className={`${classes.periodReferenceMenuItem} ${
                      periodReference === option.key
                        ? classes.periodReferenceMenuItemActive
                        : ""
                    } ${option.isDisabled ? classes.periodReferenceMenuItemDisabled : ""}`}
                    onClick={
                      option.isDisabled
                        ? undefined
                        : () => {
                            onPeriodChange(option.key);
                            setPeriodMenuOpen(false);
                          }
                    }
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Previous button */}
          <div
            className={`${classes.navButton} ${
              isFirstPage ? classes.navButtonDisabled : ""
            }`}
            onClick={isFirstPage ? undefined : onPrevious}
          >
            <ChevronLeft />
          </div>

          {/* Next button */}
          <div
            className={`${classes.navButton} ${
              isLastPage ? classes.navButtonDisabled : ""
            }`}
            onClick={isLastPage ? undefined : onNext}
          >
            <ChevronRight />
          </div>

          {/* Exit Focus Mode button (only visible in focus mode) */}
          {focusMode && (
            <div
              className={classes.navButton}
              onClick={onFocusModeToggle}
              title={t("alignment_session.actions.exit_focus_mode")}
              style={{ marginLeft: "8px" }}
            >
              <FullscreenExit />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

Navigation.propTypes = {
  sortedPages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      key: PropTypes.string.isRequired,
      order: PropTypes.number.isRequired,
    }),
  ).isRequired,
  activePage: PropTypes.number.isRequired,
  onPageClick: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  clientColor: PropTypes.string.isRequired,
  periodOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      isDisabled: PropTypes.bool,
    }),
  ).isRequired,
  periodReference: PropTypes.string.isRequired,
  onPeriodChange: PropTypes.func.isRequired,
  highlightEnabled: PropTypes.bool,
  onHighlightChange: PropTypes.func,
  showHighlightToggle: PropTypes.bool,
  focusMode: PropTypes.bool,
  onFocusModeToggle: PropTypes.func,
  dataOrchestrator: PropTypes.object,
};

Navigation.defaultProps = {
  highlightEnabled: false,
  onHighlightChange: () => {},
  showHighlightToggle: false,
  focusMode: false,
  onFocusModeToggle: () => {},
  dataOrchestrator: null,
};

export default Navigation;
